import { Link } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import SectionHeading from "@/components/base/SectionHeading";
import EpisodeCard from "@/components/feature/EpisodeCard";
import ActorCard from "@/components/feature/ActorCard";
import PlotCard from "@/components/feature/PlotCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { episodes } from "@/mocks/episodes";
import { actors } from "@/mocks/actors";
import { plots } from "@/mocks/plots";
import { siteStats } from "@/mocks/home";

export default function Home() {
  const latestEpisodes = [...episodes].sort((a, b) => b.ep - a.ep).slice(0, 6);
  const featuredPlots = plots.slice(0, 3);

  return (
    <>
      <PageMeta
        title="年轻的母亲1电视剧剧情及演员介绍 - 年轻的母亲1官网"
        description="年轻的母亲1提供最新电视剧剧情解析、演员信息和剧集更新，为您带来全面的影视剧内容体验，在这里了解年轻的母亲1的精彩故事。"
        keywords="年轻的母亲1,年轻的母亲1电视剧,年轻的母亲1剧情,年轻的母亲1演员,年轻的母亲1介绍,影视剧集,电视剧剧情"
      />

      <HeroCarousel />

      {/* 数据概览 */}
      <section className="border-b border-background-200 bg-background-50">
        <div className="mx-auto grid max-w-site grid-cols-2 gap-px bg-background-200 px-4 md:grid-cols-4 md:px-6">
          {siteStats.map((stat) => (
            <div key={stat.label} className="bg-background-50 px-4 py-6 text-center md:py-8">
              <p className="text-xl font-bold text-primary-600 md:text-3xl">{stat.value}</p>
              <p className="mt-1 text-xs text-foreground-500 md:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mx-auto max-w-site px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* 左侧主内容 */}
          <div>
            <section>
              <SectionHeading
                eyebrow="最新更新"
                title="最新更新剧集"
                description="《年轻的母亲1》全 12 集已完结，这里汇总最近更新的剧集内容与看点。"
                moreLink="/category/episodes"
              />
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {latestEpisodes.map((episode) => (
                  <EpisodeCard key={episode.id} episode={episode} />
                ))}
              </div>
            </section>

            <section className="mt-14">
              <SectionHeading
                eyebrow="演员精选"
                title="主要演员阵容"
                description="点击卡片查看演员资料、饰演角色与参演剧集。"
                moreLink="/category/actors"
              />
              <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
                {actors.map((actor) => (
                  <ActorCard key={actor.id} actor={actor} compact />
                ))}
              </div>
            </section>

            <section className="mt-14">
              <SectionHeading
                eyebrow="深度解析"
                title="剧情解析专题推荐"
                description="从人物动机到叙事结构，带你重新理解这个故事。"
                moreLink="/category/plots"
              />
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {featuredPlots.map((plot) => (
                  <PlotCard key={plot.id} plot={plot} />
                ))}
              </div>
            </section>
          </div>

          {/* 右侧固定侧栏 */}
          <aside className="space-y-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
                <i className="ri-user-star-line text-accent-500" />
                演员快捷入口
              </h3>
              <ul className="mt-4 space-y-1">
                {actors.slice(0, 5).map((actor) => (
                  <li key={actor.id}>
                    <Link
                      to={`/detail/actor/${actor.id}`}
                      className="group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-100"
                    >
                      <span className="h-11 w-11 shrink-0 overflow-hidden rounded-full bg-background-200">
                        <img src={actor.avatar} alt={actor.name} className="h-full w-full object-cover object-top" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                          {actor.name}
                        </span>
                        <span className="block truncate text-xs text-foreground-500">饰 {actor.role}</span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-300 transition-colors group-hover:text-primary-500" />
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/category/actors"
                className="mt-3 flex items-center justify-center gap-1 rounded-md border border-background-200 py-2 text-xs font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                查看全部演员
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
                <i className="ri-list-ordered text-primary-500" />
                热门章节
              </h3>
              <ol className="mt-4 space-y-1">
                {[...episodes]
                  .sort((a, b) => Number.parseFloat(b.views) - Number.parseFloat(a.views))
                  .slice(0, 5)
                  .map((episode, index) => (
                    <li key={episode.id}>
                      <Link
                        to={`/detail/episode/${episode.id}`}
                        className="group flex items-start gap-3 rounded-md p-2 transition-colors hover:bg-background-100"
                      >
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                            index < 3 ? "bg-accent-500 text-background-50" : "bg-background-200 text-foreground-500"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-foreground-800 group-hover:text-primary-600">
                            第{episode.ep}集 · {episode.title}
                          </span>
                          <span className="block text-xs text-foreground-500">{episode.views} 播放</span>
                        </span>
                      </Link>
                    </li>
                  ))}
              </ol>
            </div>

            <div className="overflow-hidden rounded-lg bg-primary-950 p-6 text-background-50">
              <h3 className="text-base font-semibold">关于本站</h3>
              <p className="mt-3 text-sm leading-relaxed text-background-200/85">
                年轻的母亲1 内容档案站，专注为剧迷整理剧情脉络、演员资料与深度解析。所有内容以静态演示数据呈现，方便你快速查阅。
              </p>
              <Link
                to="/about"
                className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                了解更多
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}